import { openDB, DBSchema, IDBPDatabase } from "idb";

interface OfflineDBSchema extends DBSchema {
  courses: {
    key: string;
    value: any;
  };
  userProfile: {
    key: string;
    value: any;
  };
  assignments: {
    key: string;
    value: any;
  };
  pendingSync: {
    key: number;
    value: {
      id?: number;
      url: string;
      method: string;
      body: any;
      timestamp: number;
    };
  };
}

const DB_NAME = "atiam-cms-offline";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<OfflineDBSchema>> | null = null;

const getDB = async (): Promise<IDBPDatabase<OfflineDBSchema>> => {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create object stores
        if (!db.objectStoreNames.contains("courses")) {
          db.createObjectStore("courses", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("userProfile")) {
          db.createObjectStore("userProfile", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("assignments")) {
          db.createObjectStore("assignments", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("pendingSync")) {
          db.createObjectStore("pendingSync", {
            keyPath: "id",
            autoIncrement: true,
          });
        }
      },
    });
  }
  return dbPromise;
};

// Store data for offline access
export const storeOfflineData = async (
  storeName: keyof OfflineDBSchema,
  data: any
) => {
  try {
    const db = await getDB();
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);

    if (Array.isArray(data)) {
      await Promise.all(data.map((item) => store.put(item)));
    } else {
      await store.put(data);
    }

    await tx.done;
  } catch (error) {
    console.error("Error storing offline data:", error);
  }
};

// Retrieve offline data
export const getOfflineData = async (
  storeName: keyof OfflineDBSchema,
  key?: string
) => {
  try {
    const db = await getDB();
    if (key) {
      return await db.get(storeName, key);
    }
    return await db.getAll(storeName);
  } catch (error) {
    console.error("Error retrieving offline data:", error);
    return null;
  }
};

// Clear offline data
export const clearOfflineData = async (storeName: keyof OfflineDBSchema) => {
  try {
    const db = await getDB();
    await db.clear(storeName);
  } catch (error) {
    console.error("Error clearing offline data:", error);
  }
};

// Queue request for background sync
export const queueSyncRequest = async (
  url: string,
  method: string,
  body: any
) => {
  try {
    const db = await getDB();
    await db.add("pendingSync", {
      url,
      method,
      body,
      timestamp: Date.now(),
    });

    // Register background sync if available
    if (
      "serviceWorker" in navigator &&
      "sync" in ServiceWorkerRegistration.prototype
    ) {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register("sync-queue");
    }
  } catch (error) {
    console.error("Error queueing sync request:", error);
  }
};

// Get pending sync requests
export const getPendingSyncRequests = async () => {
  try {
    const db = await getDB();
    return await db.getAll("pendingSync");
  } catch (error) {
    console.error("Error getting pending sync requests:", error);
    return [];
  }
};

// Remove sync request
export const removeSyncRequest = async (id: number) => {
  try {
    const db = await getDB();
    await db.delete("pendingSync", id);
  } catch (error) {
    console.error("Error removing sync request:", error);
  }
};
